import { Button, Checkbox, Dropdown, Flyout, FormField, Icon, Menu, ProgressSpinner, SplitButton, Typography } from "@bbnpm/bb-ui-framework";
import { useEffect, useRef, useState } from 'react';
import './launch.min.css';

const Launch = ({ setHasServerError }) => {
    const SITE_ADDRESS = process.env.PUBLIC_URL + '/';
    const [flyoutInstance, setFlyoutInstance] = useState({});
    const [currentRegion, setCurrentRegion] = useState(() => { return window.SETTINGS.currentRegion });
    const [showPageProgress, setShowPageProgress] = useState(false);
    const [excelChecked, setExcelChecked] = useState(() => { return window.SETTINGS.useExcel == 1 });
    const [application, setApplication] = useState(() => { return window.SETTINGS.defaultApplication });
    const [applicationDisplay, setApplicationDisplay] = useState(() => { return window.SETTINGS.defaultApplication });
    const [setHasError] = useState(false);
    const [autoLaunchEnabled] = useState(() => { return window.SETTINGS.autoLaunchEnabled == 1 });
    const [autoLaunchAppName] = useState(() => { return window.SETTINGS.autoLaunchAppName });
    const [autoLaunchAppDisplay] = useState(() => { return window.SETTINGS.autoLaunchAppDisplay });
    const [autoLaunchType] = useState(() => { return window.SETTINGS.autoLaunchType });
    const [autoLaunchExcel] = useState(() => { return window.SETTINGS.autoLaunchExcel });
    const [launchProtocols] = useState(() => { return window.SETTINGS.launchProtocols});
    const [defaultLaunchProtocol] = useState(() => { return window.SETTINGS.defaultLaunchProtocol});
    const [spinnerLabel, setSpinnerLabel] = useState('Launching, please wait...');

    const launchButtonRef = useRef()
    useEffect(() => {
        window.addEventListener("keydown", e => {
            if (e.key === "Enter") {
                launchButtonRef.current.click()
            }
        })
    }, [])

    useEffect(() => {
        if (autoLaunchEnabled) {
            var use_excel = autoLaunchExcel.length > 0 ?
                (autoLaunchExcel == 1 ? true : false) : excelChecked

            downloadFile(SITE_ADDRESS + 'launch_app', currentRegion,
                autoLaunchType ? autoLaunchType : 'citrix',
                use_excel,
                autoLaunchAppName ? autoLaunchAppName : application,
                autoLaunchAppDisplay ? autoLaunchAppDisplay : applicationDisplay, 1,
                waitToConnect(), setFileText)
        }
    }, [])

    useEffect(() => {
        var exp = new Date()
        exp.setSeconds(exp.getSeconds() + parseInt(window.SETTINGS.expirationSeconds));

        setInterval(() => {
            var now = new Date()
            if (now >= exp) {
                window.location.href = SITE_ADDRESS + "logout";
            }
        }, 5000)
    }, [SITE_ADDRESS]);

    const setFileText = (fileName) => {
        setSpinnerLabel(`Open downloaded ${fileName} file to launch Bloomberg Terminal`)
    };

    // show progress spinner when attempting launch, hide when connected
    const waitToConnect = () => {
        setHasServerError(false);

        let sessionActiveInterval = setInterval(checkConnection, 5000);
        setShowPageProgress(true);

        setTimeout(() => {
            clearInterval(sessionActiveInterval);
            setShowPageProgress(false);
            setSpinnerLabel('Launching, please wait...')
        }, 90000); // timeout after 90 seconds

        function checkConnection() {
            try {
                fetch(SITE_ADDRESS + "is_session_active", {credentials: 'same-origin'})
                    .then(async (response) => {
                        if (response.status === 200) {
                            let json = await response.json();

                            if (json.is_active) {
                                clearInterval(sessionActiveInterval);
                                setShowPageProgress(false);
                            }
                        } else {
                            let location = response.headers.get('Location');
                            document.location.href = location ?? "/"
                        }
                    });
            } catch (e) {
                setHasError(() => { throw e });
            }
        }
    }

    return (
        <div className="launch">
            <div className='content'>
                <div className="top">
                    <Typography.SectionTitle>Launch</Typography.SectionTitle>
                    <p>
                        {window.SETTINGS.name}
                        {(window.SETTINGS.location.length > 0) && <br />}
                        {window.SETTINGS.location}
                    </p>
                </div>
                <form>
                    {window.SETTINGS.applicationOptions.length > 1 &&
                        <FormField htmlForm="bba-application-dropdown" label="Select Application">
                            <Dropdown
                                id="bba-application-dropdown"
                                name="application"
                                defaultValue={window.SETTINGS.defaultApplication}
                                onItemSelect={(e) => {
                                    setApplication(e.value);
                                    setApplicationDisplay(e.label);
                                }}
                                options={window.SETTINGS.applicationOptions}
                                optionRenderer={(option) => (
                                    <div>{option.labelDropdown ? option.labelDropdown : option.label}</div>
                                )}
                            />
                        </FormField>
                    }

                    <div className='row'>
                        <Checkbox
                            label={<span>Launch with the <strong>Bloomberg Excel add-in</strong></span>}
                            defaultChecked={excelChecked}
                            onClick={(event) => setExcelChecked(event.target.checked)}
                        />
                        <Flyout
                            content={
                                <div className="excelTooltip">
                                    With the Bloomberg Excel Add-In, you can use a remote version of Microsoft Excel that interacts with Bloomberg API data.
                                    <a href={SITE_ADDRESS + "help#section_excel"} target='_blank' rel='noreferrer'>
                                        <Button
                                            onClick={() => flyoutInstance.hide()}
                                        >
                                            Learn More
                                        </Button>
                                    </a>
                                </div>
                            }
                            onCreate={setFlyoutInstance}
                        ><Icon name="info-circle" /></Flyout>
                    </div>

                    <SplitButton
                        buttonProps={{ ref: launchButtonRef, children: 'Launch', onClick: () => downloadFile(SITE_ADDRESS + 'launch_app', currentRegion, defaultLaunchProtocol, excelChecked, application, applicationDisplay, 0, waitToConnect(), setFileText) }}
                        kind="primary"
                        menuItems={[
                            launchProtocols.includes("citrix") && {label: 'Launch with Citrix Receiver (Recommended)', onSelect: () => downloadFile(SITE_ADDRESS + 'launch_app', currentRegion, 'citrix', excelChecked, application, applicationDisplay, 0, waitToConnect(), setFileText) },
                            launchProtocols.includes("rdp") && { label: 'Launch with RDP', onSelect: () => downloadFile(SITE_ADDRESS + 'launch_app', currentRegion, 'rdp', excelChecked, application, applicationDisplay, 0, waitToConnect(), setFileText) },
                            { label: 'Launch with the Browser', onSelect: () => downloadFile(SITE_ADDRESS + 'launch_app', currentRegion, 'browser', excelChecked, application, applicationDisplay, 0, waitToConnect(), setFileText) }
                        ].filter(function( element ) {return !!element;})}
                    />

                </form>
                <div className='bottom'>
                    <p className="link-wrapper">
                        Need to install Citrix?&nbsp;
                        <a href={SITE_ADDRESS + 'help'} target='_blank' rel='noreferrer'>Learn More</a>
                    </p>
                    <div className='region'>
                        <div className="row">
                            <Flyout content={<div className="countryTooltip">Select your region to help us give you the best possible experience.</div>}>
                                <Icon name="info-circle" />
                            </Flyout>
                            <span>
                                <strong>Current Region:</strong>
                                <Menu
                                    className='bbui-text-dropdown'
                                    triggerText={currentRegion}
                                    hideIcon={true}
                                    menuItems={window.SETTINGS.regions.map((str) => {
                                        return {
                                            label: str,
                                            onSelect: () => {
                                                setCurrentRegion(str)
                                            }
                                        }
                                    })}
                                />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div id="page_progress" style={{ display: showPageProgress ? "flex" : "none" }}>
                <ProgressSpinner size="large" label={spinnerLabel}/>
            </div>
        </div>
    );
}


async function downloadFile(url, currentRegion, launchType, excelChecked, application, applicationDisplay, autoLaunch, callback, setFileTextCallback) {
    var fetchUrl = url + "?launchType=" + launchType + "&region=" + currentRegion +
        "&excel=" + excelChecked + "&application=" + application +
        "&applicationDisplay=" + applicationDisplay + "&auto=" + autoLaunch

    var site_address = process.env.PUBLIC_URL + '/'
    let milliseconds = (new Date()).getTime()

    // If launchType = browser we're opening the html5_window before we
    // make the ica fetch so it isn't blocked by safari https://stackoverflow.com/a/39387533
    var html5_window = launchType == "browser"
        ? window.open(site_address+ 'html5/src/SessionWindow.html?launchid=' + milliseconds, "_blank")
        : null;

    fetch(fetchUrl, {credentials: 'same-origin'})
        .then(async (response) => {
            let contentType = response.headers.get('Content-Type');
            let badFileType = (contentType !== 'application/rdp') && (contentType !== 'application/x-ica') && (contentType !== 'application/ica')
            if ((!response.ok) || badFileType) {
                if (response.status === 200) {
                    let url = new URL(response.url)
                    if (url.pathname.toUpperCase() === "/LAUNCH") {
                        let html = await response.text();
                        document.open();
                        document.write(html);
                        return
                    } else if (url.pathname.toUpperCase() === "/ERROR") {
                        document.location.href = response.url
                        return
                    }
                } else if (response.status === 401) {
                    let location = response.headers.get('Location');
                    document.location.href = location ?? "/"
                    return
                }

                document.location.href = "/error"
                return
            }
            let banner = document.querySelectorAll("[class*='bbui-banner error']")[0]
            if (banner) banner.remove()
            return response.blob();
        })
        .then((data) => {
            if (data === undefined){ if (html5_window){ html5_window.close(); } return;}
            let csvFile = new Blob([data], { type: "application/octet-stream" });
            let fileName = (data.type !== 'application/x-ica' && data.type !== 'application/ica') ? "bba.rdp"  : "bba.ica";
            if (launchType === 'browser') {
                return html5Helper(milliseconds, html5_window, csvFile);
            }
            if (window.navigator.msSaveBlob) {
                window.navigator.msSaveBlob(csvFile, fileName);
            } else {
                setFileTextCallback(fileName);
                let a = document.createElement('a');
                let url = window.URL.createObjectURL(csvFile);
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
            }
        })
        .catch((err) => {
            console.error(err);
            if (html5_window) html5_window.close()
            window.notif.addError({ message: (err.flask_message) ? `Error: ${err.flask_message}` : "Error: Please refresh the browser and try again." });
        })
        .finally(() => {
            typeof callback === "function" && callback();
        });
}

async function html5Helper(milliseconds, html5_window, ica) {
    var html5LaunchData = {}
    var ica_text = await new Response(ica).text()

    var e, o, h;
    var s = ica_text.split('\r\n');
    var ica_object = {};

    for (e = 0; e < s.length; e++) {
        o = s[e].split('=', 2)
        if (o.length === 2) {
            ica_object[o[0]] = o[1]
        }
        if (o[0] === 'LaunchReference') {
            h = s[e].indexOf('=')
            ica_object[o[0]] = s[e].substring(h + 1)
        }
    }
    ica_object.IconUrl = 'data:image/png;base64,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'
    ica_object.clientPreferences = ''
    ica_object.UILocale = 'en'
    html5LaunchData[milliseconds] = ica_object
    window.html5LaunchData = html5LaunchData
    html5_window.icaDwldComplete = true
}

export default Launch;
